import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Upload, MessageCircle, Copy, LogOut, User, ChevronDown, ChevronUp, Key, Mail, RefreshCw, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminNav from './AdminNav';

const AdminPanel = () => {
  const { t } = useTranslation();
  const { user, logout, getAuthHeaders } = useAuth();
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt: '',
    companyName: '',
    greeting: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    developerMode: false,
    model: 'gpt-5.2',
    temperature: 0.7,
    primaryColor: '#3b82f6',
    catalogMode: 'standard'
  });
  const [document, setDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [activeEmbedTab, setActiveEmbedTab] = useState('script');
  const [expandedChatbotId, setExpandedChatbotId] = useState(null);
  const [domainInputs, setDomainInputs] = useState({}); // Track domain input per chatbot
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordChatbotName, setPasswordChatbotName] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isResendingCredentials, setIsResendingCredentials] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmChatbotId, setConfirmChatbotId] = useState(null);
  const [confirmChatbotName, setConfirmChatbotName] = useState(null);
  const [pricingConfig, setPricingConfig] = useState({
    businessZip: '',
    distanceFactorPer10km: '',
    noParkingFactor: '',
    salePercent: '',
    hidePrices: false,
    showEstimateRange: false,
    estimateRangePercent: '15',
    showPriceListDescription: true
  });
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptTemplateId, setSelectedPromptTemplateId] = useState('');

  useEffect(() => {
    fetchChatbots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCreating || isEditing) {
      fetchPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, isEditing]);

  const fetchPrompts = async () => {
    try {
      const response = await fetch('/api/admin/prompts', {
        headers: getAuthHeaders()
      });
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setPrompts(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  };

  const fetchChatbots = async () => {
    try {
      const response = await fetch('/api/admin/chatbots', {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        logout();
        return;
      }
      
      // Check if response is ok before parsing
      if (!response.ok) {
        console.error('Failed to fetch chatbots:', response.status, response.statusText);
        setChatbots([]);
        return;
      }
      
      // Parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setChatbots([]);
        return;
      }
      
      // Ensure data is an array (handle both direct array and wrapped responses)
      const chatbotsArray = Array.isArray(data) ? data : (data.chatbots || []);
      
      if (!Array.isArray(chatbotsArray)) {
        console.error('Invalid response format - expected array, got:', typeof chatbotsArray);
        setChatbots([]);
        return;
      }
      
      setChatbots(chatbotsArray);
    } catch (error) {
      console.error('Error fetching chatbots:', error);
      setChatbots([]); // Set to empty array on error
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setDocument(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that a JSON services file is required for creation (not for editing)
    if (!isEditing && !document) {
      window.alert(t('admin.panel.uploadServicesRequiredAlert'));
      return;
    }
    
    setIsLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Add allowedDomains as JSON string
      formDataToSend.append('allowedDomains', JSON.stringify(allowedDomains));

      // Add pricingConfig (location-based price factors)
      const pc = {};
      if (pricingConfig.businessZip?.trim()) pc.businessZip = pricingConfig.businessZip.trim();
      const df = parseFloat(pricingConfig.distanceFactorPer10km);
      if (!isNaN(df) && df >= 0) pc.distanceFactorPer10km = df;
      const npf = parseFloat(pricingConfig.noParkingFactor);
      if (!isNaN(npf) && npf >= 1) pc.noParkingFactor = npf;
      const sale = parseFloat(pricingConfig.salePercent);
      if (!isNaN(sale) && sale > 0 && sale <= 100) pc.salePercent = sale;
      if (pricingConfig.hidePrices) pc.hidePrices = true;
      if (pricingConfig.showPriceListDescription === false) pc.showPriceListDescription = false;
      if (pricingConfig.showEstimateRange) pc.showEstimateRange = true;
      const rangePct = parseFloat(pricingConfig.estimateRangePercent);
      if (pricingConfig.showEstimateRange) {
        pc.estimateRangePercent = !isNaN(rangePct) && rangePct >= 0 && rangePct <= 100 ? rangePct : 15;
      } else if (!isNaN(rangePct) && rangePct >= 0 && rangePct <= 100) {
        pc.estimateRangePercent = rangePct;
      }
      formDataToSend.append('pricingConfig', JSON.stringify(Object.keys(pc).length ? pc : null));

      // Add JSON services file (for creation, or when editing and user selected a new file to replace)
      if (document) {
        formDataToSend.append('documents', document);
      }

      const url = isEditing 
        ? `/api/admin/chatbots/${selectedChatbot.id}`
        : '/api/admin/chatbots';
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: formDataToSend
      });

      if (response.ok) {
        const data = await response.json();
        await fetchChatbots();
        
        // If this was a new chatbot creation, show password modal
        if (!isEditing && data.generatedPassword) {
          setGeneratedPassword(data.generatedPassword);
          setPasswordChatbotName(data.name || formData.name);
          setShowPasswordModal(true);
        }
        
        resetForm();
      } else if (response.status === 401) {
        logout();
      } else {
        const error = await response.json();
        window.alert('Error: ' + error.error);
      }
    } catch (error) {
      console.error('Error saving chatbot:', error);
      window.alert(t('admin.panel.errorSavingChatbot'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (chatbot) => {
    setSelectedChatbot(chatbot);
    setFormData({
      name: chatbot.name,
      description: chatbot.description,
      prompt: chatbot.prompt,
      companyName: chatbot.companyName,
      greeting: chatbot.greeting || '',
      contactEmail: chatbot.contactEmail,
      contactPhone: chatbot.contactPhone,
      website: chatbot.website,
      developerMode: chatbot.developerMode || false,
      model: chatbot.model || 'gpt-5.2',
      temperature: chatbot.temperature ?? 0.7,
      primaryColor: chatbot.primaryColor || '#3b82f6',
      catalogMode: chatbot.catalogMode === 'catering' ? 'catering' : 'standard'
    });
    setAllowedDomains(chatbot.allowedDomains || []);
    setDocument(null); // Clear any document selection when editing
    const pc = chatbot.pricingConfig || {};
    setPricingConfig({
      businessZip: pc.businessZip || '',
      distanceFactorPer10km: pc.distanceFactorPer10km != null ? String(pc.distanceFactorPer10km) : '',
      noParkingFactor: pc.noParkingFactor != null ? String(pc.noParkingFactor) : '',
      salePercent: pc.salePercent != null ? String(pc.salePercent) : '',
      hidePrices: pc.hidePrices === true,
      showEstimateRange: pc.showEstimateRange === true,
      estimateRangePercent: pc.estimateRangePercent != null ? String(pc.estimateRangePercent) : '15',
      showPriceListDescription: pc.showPriceListDescription !== false
    });
    setIsEditing(true);
    setIsCreating(true);
    setExpandedChatbotId(null); // Close expanded view when editing
  };

  const handleDelete = async (chatbotId) => {
    if (!window.confirm(t('admin.panel.confirmDelete'))) return;

    try {
      const response = await fetch(`/api/admin/chatbots/${chatbotId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchChatbots();
      } else if (response.status === 401) {
        logout();
      } else {
        const error = await response.json();
        window.alert('Error: ' + error.error);
      }
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      window.alert(t('admin.panel.errorDeletingChatbot'));
    }
  };

  const handleResetPassword = async (chatbotId, chatbotName) => {
    // Show confirmation modal instead of window.confirm
    setConfirmAction('reset');
    setConfirmChatbotId(chatbotId);
    setConfirmChatbotName(chatbotName);
    setShowConfirmModal(true);
  };

  const executeResetPassword = async () => {
    if (!confirmChatbotId || !confirmChatbotName) return;
    
    const chatbotId = confirmChatbotId;
    const chatbotName = confirmChatbotName;
    
    setShowConfirmModal(false);
    setIsResettingPassword(true);
    
    try {
      const response = await fetch(`/api/admin/chatbots/${chatbotId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedPassword(data.newPassword);
        setPasswordChatbotName(chatbotName);
        setShowPasswordModal(true);
      } else if (response.status === 401) {
        logout();
      } else {
        const error = await response.json();
        window.alert('Error: ' + error.error);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      window.alert(t('admin.panel.errorResettingPassword'));
    } finally {
      setIsResettingPassword(false);
      setConfirmAction(null);
      setConfirmChatbotId(null);
      setConfirmChatbotName(null);
    }
  };

  const handleResendCredentials = async (chatbotId, chatbotName) => {
    // Show confirmation modal instead of window.confirm
    setConfirmAction('resend');
    setConfirmChatbotId(chatbotId);
    setConfirmChatbotName(chatbotName);
    setShowConfirmModal(true);
  };

  const executeResendCredentials = async () => {
    if (!confirmChatbotId || !confirmChatbotName) return;
    
    const chatbotId = confirmChatbotId;
    const chatbotName = confirmChatbotName;
    
    setShowConfirmModal(false);
    setIsResendingCredentials(true);
    
    try {
      const response = await fetch(`/api/admin/chatbots/${chatbotId}/resend-credentials`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.newPassword) {
          // If email failed, show password modal
          setGeneratedPassword(data.newPassword);
          setPasswordChatbotName(chatbotName);
          setShowPasswordModal(true);
          window.alert(t('admin.panel.passwordResetEmailFailed'));
        } else {
          window.alert(t('admin.panel.credentialsSentSuccess', { email: data.email }));
        }
      } else if (response.status === 401) {
        logout();
      } else {
        const error = await response.json();
        window.alert('Error: ' + error.error);
      }
    } catch (error) {
      console.error('Error resending credentials:', error);
      window.alert(t('admin.panel.errorResendingCredentials'));
    } finally {
      setIsResendingCredentials(false);
      setConfirmAction(null);
      setConfirmChatbotId(null);
      setConfirmChatbotName(null);
    }
  };

  const copyPasswordToClipboard = async (password) => {
    try {
      // Check if clipboard API is available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(password);
        return true;
      }
    } catch (err) {
      // Continue to fallback
    }
    
    // Fallback for older browsers or non-secure contexts
    // Check if document is available before using it
    if (typeof document === 'undefined' || document === null) {
      console.error('Document is not available');
      return false;
    }
    
    try {
      const textArea = document.createElement('textarea');
      textArea.value = password;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    } catch (err) {
      console.error('Failed to copy password to clipboard:', err);
      return false;
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      prompt: '',
      companyName: '',
      greeting: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      developerMode: false,
      model: 'gpt-5.2',
      temperature: 0.7,
      primaryColor: '#3b82f6',
      catalogMode: 'standard'
    });
    setDocument(null);
    setSelectedChatbot(null);
    setIsCreating(false);
    setIsEditing(false);
    setAllowedDomains([]);
    setNewDomain('');
    setPricingConfig({
      businessZip: '',
      distanceFactorPer10km: '',
      noParkingFactor: '',
      salePercent: '',
      hidePrices: false,
      showEstimateRange: false,
      estimateRangePercent: '15',
      showPriceListDescription: true
    });
    setSelectedPromptTemplateId('');
  };

  const handlePromptTemplateSelect = (e) => {
    const id = e.target.value;
    setSelectedPromptTemplateId(id);
    if (id) {
      const template = prompts.find((p) => p.id === id);
      if (template) {
        setFormData((prev) => ({ ...prev, prompt: template.content }));
      }
    }
  };

  const getScriptTagEmbedCode = (chatbotId, primaryColor = '#3b82f6') => {
    const apiUrl = window.location.origin;
    return `<script src="${apiUrl}/widget/widget.js" 
        data-chatbot-id="${chatbotId}"
        data-api-url="${apiUrl}"
        data-primary-color="${primaryColor}"
        data-position="bottom-right">
</script>`;
  };

  const getIframeEmbedCode = (chatbotId, primaryColor = '#3b82f6') => {
    const apiUrl = window.location.origin;
    return `<!-- Recommended: use the script tag embed (Floating tab) instead.
A fixed-size iframe blocks clicks on the host page when the chat is closed.
The script loader resizes the iframe dynamically so only the launcher is clickable. -->
<script src="${apiUrl}/widget/widget.js"
        data-chatbot-id="${chatbotId}"
        data-api-url="${apiUrl}"
        data-primary-color="${primaryColor}"
        data-position="bottom-right">
</script>`;
  };

  const getIframeStepperEmbedCode = (chatbotId) => {
    const embedUrl = `${window.location.origin}/embed/${chatbotId}/stepper`;
    return `<!-- Inline stepper widget: full-width section (Intro → Chat → Offer) -->
<div style="width:100%;max-width:1152px;margin:0 auto;padding:1rem;">
  <iframe src="${embedUrl}" width="100%" height="800" frameborder="0" style="border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,0.15);"></iframe>
</div>`;
  };

  const getScriptTagStepperEmbedCode = (chatbotId, primaryColor = '#3b82f6') => {
    const apiUrl = window.location.origin;
    return `<script src="${apiUrl}/widget/widget.js"
        data-chatbot-id="${chatbotId}"
        data-api-url="${apiUrl}"
        data-primary-color="${primaryColor}"
        data-mode="stepper">
</script>`;
  };

  const getReactEmbedCode = (chatbotId, primaryColor = '#3b82f6') => {
    const apiUrl = window.location.origin;
    return `import ChatbotWidget from '${apiUrl}/components/ReactChatbot.jsx';

<ChatbotWidget 
  chatbotId="${chatbotId}"
  apiBaseUrl="${apiUrl}"
  primaryColor="${primaryColor}"
  position="bottom-right"
/>`;
  };

  const getVueEmbedCode = (chatbotId, primaryColor = '#3b82f6') => {
    const apiUrl = window.location.origin;
    return `<template>
  <ChatbotWidget 
    :chatbot-id="'${chatbotId}'"
    :api-base-url="'${apiUrl}'"
    :primary-color="'${primaryColor}'"
    :position="'bottom-right'"
  />
</template>

<script>
import ChatbotWidget from '${apiUrl}/components/VueChatbot.vue';
export default {
  components: { ChatbotWidget }
};
</script>`;
  };

  const getAngularEmbedCode = (chatbotId, primaryColor = '#3b82f6') => {
    const apiUrl = window.location.origin;
    return `<app-chatbot-widget
  [chatbotId]="'${chatbotId}'"
  [apiBaseUrl]="'${apiUrl}'"
  [primaryColor]="'${primaryColor}'"
  [position]="'bottom-right'">
</app-chatbot-widget>`;
  };

  const getWordPressInstructions = (chatbotId) => {
    const apiUrl = window.location.origin;
    return `1. Download the WordPress plugin from: ${apiUrl}/components/wordpress-plugin.php
2. Upload it to your WordPress site's plugins directory
3. Activate the plugin in WordPress admin
4. Go to Settings > Chatbot Widget
5. Enter your Chatbot ID: ${chatbotId}
6. Enter API Base URL: ${apiUrl}
7. Configure colors and position
8. Save settings`;
  };

  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      // Continue to fallback
    }
    
    // Fallback for older browsers or non-secure contexts
    // Check if document is available before using it
    if (typeof document === 'undefined' || document === null) {
      console.error('Document is not available');
      return false;
    }
    
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      return false;
    }
  };

  const getChatbotUrl = (chatbotId) => {
    return `${window.location.origin}/embed/${chatbotId}`;
  };

  const copyEmbedCode = async (chatbotId, type = 'script', primaryColor = '#3b82f6') => {
    let embedCode = '';
    switch(type) {
      case 'script':
        embedCode = getScriptTagEmbedCode(chatbotId, primaryColor);
        break;
      case 'iframe':
        embedCode = getIframeEmbedCode(chatbotId, primaryColor);
        break;
      case 'stepper':
        embedCode = getIframeStepperEmbedCode(chatbotId);
        break;
      case 'react':
        embedCode = getReactEmbedCode(chatbotId, primaryColor);
        break;
      case 'vue':
        embedCode = getVueEmbedCode(chatbotId, primaryColor);
        break;
      case 'angular':
        embedCode = getAngularEmbedCode(chatbotId, primaryColor);
        break;
      default:
        embedCode = getScriptTagEmbedCode(chatbotId, primaryColor);
    }
    
    const success = await copyToClipboard(embedCode);
    if (success) {
      window.alert(t('admin.panel.embedCodeCopied'));
    } else {
      window.prompt(t('common.copy'), embedCode);
    }
  };

  const handleAddDomain = (chatbotId) => {
    const domain = (domainInputs[chatbotId] || '').trim();
    if (domain) {
      const chatbot = chatbots.find(cb => cb.id === chatbotId);
      const currentDomains = chatbot?.allowedDomains || [];
      if (!currentDomains.includes(domain)) {
        const updatedDomains = [...currentDomains, domain];
        handleSaveDomains(chatbotId, updatedDomains);
        setDomainInputs({ ...domainInputs, [chatbotId]: '' });
      }
    }
  };

  const handleRemoveDomain = (chatbotId, domain) => {
    const chatbot = chatbots.find(cb => cb.id === chatbotId);
    const currentDomains = chatbot?.allowedDomains || [];
    const updatedDomains = currentDomains.filter(d => d !== domain);
    handleSaveDomains(chatbotId, updatedDomains);
  };

  const handleSaveDomains = async (chatbotId, domains) => {
    try {
      const response = await fetch(`/api/admin/chatbots/${chatbotId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allowedDomains: domains })
      });

      if (response.ok) {
        await fetchChatbots();
      } else {
        const error = await response.json();
        window.alert('Error: ' + error.error);
      }
    } catch (error) {
      console.error('Error saving domains:', error);
      window.alert(t('admin.panel.errorSavingDomains'));
    }
  };

  const toggleChatbotExpansion = (chatbotId) => {
    setExpandedChatbotId(expandedChatbotId === chatbotId ? null : chatbotId);
    if (expandedChatbotId !== chatbotId) {
      setActiveEmbedTab('script');
    }
  };

  const copyChatbotUrl = async (chatbotId) => {
    const url = getChatbotUrl(chatbotId);
    const success = await copyToClipboard(url);
    if (success) {
      window.alert(t('admin.panel.chatbotLinkCopied'));
    } else {
      window.prompt(t('common.copy'), url);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.panel.title')}</h1>
              <p className="text-gray-600 mt-2">{t('admin.panel.subtitle')}</p>
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <User size={16} />
                    <span className="font-medium">{user.name}</span>
                    <span className="text-gray-400">({user.email})</span>
                  </div>
                </div>
              )}
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-2 transition"
                title={t('common.logout')}
              >
                <LogOut size={18} />
                <span>{t('common.logout')}</span>
              </button>
              <button
                onClick={() => setIsCreating(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={20} />
                {t('admin.panel.createChatbot')}
              </button>
            </div>
          </div>
        </div>

        <AdminNav />

        <div className="w-full">
          {/* Chatbots List */}
          <div>
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">{t('admin.panel.yourChatbots')}</h2>
              </div>
              <div className="p-6">
                {!Array.isArray(chatbots) || chatbots.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">{t('admin.panel.noChatbotsYet')}</p>
                    <button
                      onClick={() => setIsCreating(true)}
                      className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      {t('admin.panel.createFirstChatbot')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(Array.isArray(chatbots) ? chatbots : []).map((chatbot) => {
                      const isExpanded = expandedChatbotId === chatbot.id;
                      const chatbotDomains = chatbot.allowedDomains || [];
                      const domainInput = domainInputs[chatbot.id] || '';
                      
                      return (
                        <div key={chatbot.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Chatbot Header */}
                          <div className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900">{chatbot.name}</h3>
                                <p className="text-sm text-gray-600 mt-1">{chatbot.description}</p>
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs text-gray-500">
                                    {t('admin.panel.created', { date: new Date(chatbot.createdAt).toLocaleDateString() })}
                                  </p>
                                  <p className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block">
                                    ID: {chatbot.id}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleChatbotExpansion(chatbot.id)}
                                  className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                                  title={isExpanded ? t('common.collapse') : t('common.expand')}
                                >
                                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                                <button
                                  onClick={() => handleEdit(chatbot)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                  title={t('admin.panel.editChatbot')}
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDelete(chatbot.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                                  title={t('admin.panel.deleteChatbot')}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            {chatbot.services && chatbot.services.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs text-gray-500">{t('admin.panel.servicesCount', { count: chatbot.services.length })}</p>
                              </div>
                            )}
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-6">
                              {/* Allowed Domains Section */}
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('admin.panel.allowedDomains')}</h4>
                                <p className="text-xs text-gray-600 mb-3">
                                  {t('admin.panel.allowedDomainsHint')}
                                </p>
                                <div className="flex gap-2 mb-3">
                                  <input
                                    type="text"
                                    value={domainInput}
                                    onChange={(e) => setDomainInputs({ ...domainInputs, [chatbot.id]: e.target.value })}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddDomain(chatbot.id);
                                      }
                                    }}
                                    placeholder={t('admin.panel.placeholderDomain')}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                  <button
                                    onClick={() => handleAddDomain(chatbot.id)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                                  >
                                    {t('common.add')}
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  {chatbotDomains.map((domain, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                      <span className="text-sm text-gray-700">{domain}</span>
                                      <button
                                        onClick={() => handleRemoveDomain(chatbot.id, domain)}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                      >
                                        {t('common.remove')}
                                      </button>
                                    </div>
                                  ))}
                                  {chatbotDomains.length === 0 && (
                                    <p className="text-xs text-gray-500 italic">{t('admin.panel.noDomainsAdded')}</p>
                                  )}
                                </div>
                              </div>

                              {/* Customer Portal Access Section */}
                              {chatbot.contactEmail && (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('admin.panel.customerPortalAccess')}</h4>
                                  <p className="text-xs text-gray-600 mb-3">
                                    {t('admin.panel.customerPortalHint')}
                                  </p>
                                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Reset Password button clicked', { id: chatbot.id, name: chatbot.name });
                                        if (chatbot.id && chatbot.name) {
                                          handleResetPassword(chatbot.id, chatbot.name);
                                        } else {
                                          console.error('Missing chatbot id or name', chatbot);
                                          alert(t('admin.panel.missingChatbotInfo'));
                                        }
                                      }}
                                      disabled={isResettingPassword || isResendingCredentials || !chatbot.id || !chatbot.name}
                                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RefreshCw className={`w-4 h-4 ${isResettingPassword ? 'animate-spin' : ''}`} />
                                      {t('admin.panel.resetPassword')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Resend Credentials button clicked', { id: chatbot.id, name: chatbot.name });
                                        if (chatbot.id && chatbot.name) {
                                          handleResendCredentials(chatbot.id, chatbot.name);
                                        } else {
                                          console.error('Missing chatbot id or name', chatbot);
                                          alert(t('admin.panel.missingChatbotInfo'));
                                        }
                                      }}
                                      disabled={isResettingPassword || isResendingCredentials || !chatbot.id || !chatbot.name}
                                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Mail className={`w-4 h-4 ${isResendingCredentials ? 'animate-spin' : ''}`} />
                                      {t('admin.panel.resendCredentials')}
                                    </button>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {t('admin.panel.loginUrl')} <span className="font-mono">{window.location.origin}/customer/login</span>
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {t('admin.panel.email')} <span className="font-mono">{chatbot.contactEmail}</span>
                                  </p>
                                </div>
                              )}

                              {/* Embed Code Section */}
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('admin.panel.embedCode')}</h4>
                                
                                {/* Tabs */}
                                <div className="flex gap-2 mb-4 border-b border-gray-200 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                                  {['script', 'iframe', 'stepper', 'react', 'vue', 'angular', 'wordpress'].map((tab) => (
                                    <button
                                      key={tab}
                                      onClick={() => setActiveEmbedTab(tab)}
                                      className={`px-4 py-2 text-sm font-medium capitalize whitespace-nowrap flex-shrink-0 ${
                                        activeEmbedTab === tab
                                          ? 'border-b-2 border-blue-600 text-blue-600'
                                          : 'text-gray-600 hover:text-gray-900'
                                      }`}
                                    >
                                      {tab}
                                    </button>
                                  ))}
                                </div>

                                {/* Embed Code Display */}
                                <div className="mb-3">
                                  {activeEmbedTab === 'script' && (
                                    <textarea
                                      readOnly
                                      value={getScriptTagEmbedCode(chatbot.id, chatbot.primaryColor || '#3b82f6')}
                                      className="w-full h-32 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                    />
                                  )}
                                  {activeEmbedTab === 'iframe' && (
                                    <textarea
                                      readOnly
                                      value={getIframeEmbedCode(chatbot.id, chatbot.primaryColor || '#3b82f6')}
                                      className="w-full h-32 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                    />
                                  )}
                                  {activeEmbedTab === 'stepper' && (
                                    <div className="space-y-3">
                                      <div>
                                        <p className="text-xs font-medium text-gray-700 mb-1">Inline iframe (full-width section)</p>
                                        <textarea
                                          readOnly
                                          value={getIframeStepperEmbedCode(chatbot.id)}
                                          className="w-full h-28 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                        />
                                      </div>
                                      <div>
                                        <p className="text-xs font-medium text-gray-700 mb-1">Script tag (inline widget)</p>
                                        <textarea
                                          readOnly
                                          value={getScriptTagStepperEmbedCode(chatbot.id, chatbot.primaryColor || '#3b82f6')}
                                          className="w-full h-24 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {activeEmbedTab === 'react' && (
                                    <textarea
                                      readOnly
                                      value={getReactEmbedCode(chatbot.id, chatbot.primaryColor || '#3b82f6')}
                                      className="w-full h-40 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                    />
                                  )}
                                  {activeEmbedTab === 'vue' && (
                                    <textarea
                                      readOnly
                                      value={getVueEmbedCode(chatbot.id, chatbot.primaryColor || '#3b82f6')}
                                      className="w-full h-40 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                    />
                                  )}
                                  {activeEmbedTab === 'angular' && (
                                    <textarea
                                      readOnly
                                      value={getAngularEmbedCode(chatbot.id, chatbot.primaryColor || '#3b82f6')}
                                      className="w-full h-32 p-3 border border-gray-300 rounded text-xs font-mono bg-white"
                                    />
                                  )}
                                  {activeEmbedTab === 'wordpress' && (
                                    <div className="p-3 border border-gray-300 rounded bg-white">
                                      <pre className="text-xs whitespace-pre-wrap">{getWordPressInstructions(chatbot.id)}</pre>
                                    </div>
                                  )}
                                </div>

                                <button
                                  onClick={async () => {
                                    if (activeEmbedTab === 'wordpress') {
                                      const success = await copyToClipboard(getWordPressInstructions(chatbot.id));
                                      if (success) {
                                        window.alert(t('admin.panel.wordPressInstructionsCopied'));
                                      } else {
                                        window.alert(t('admin.panel.copyFailed'));
                                      }
                                    } else {
                                      copyEmbedCode(chatbot.id, activeEmbedTab, chatbot.primaryColor || '#3b82f6');
                                    }
                                  }}
                                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                >
                                  {activeEmbedTab === 'wordpress' ? t('admin.panel.copyInstructions') : t('admin.panel.copyCode')}
                                </button>
                                <p className="mt-2 text-xs text-gray-500">
                                  {t('admin.panel.embedIntegrationHint')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Create/Edit Modal */}
        {isCreating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditing ? t('admin.panel.editChatbotTitle') : t('admin.panel.createNewChatbot')}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.panel.chatbotName')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.panel.companyName')}
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.greeting')}
                  </label>
                  <textarea
                    name="greeting"
                    value={formData.greeting}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder={t('admin.panel.greetingPlaceholder')}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('admin.panel.greetingHint')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.description')}
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.selectPromptTemplate')}
                  </label>
                  <select
                    value={selectedPromptTemplateId}
                    onChange={handlePromptTemplateSelect}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                  >
                    <option value="">{t('admin.panel.noTemplate')}</option>
                    {prompts.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.aiPrompt')}
                  </label>
                  <textarea
                    name="prompt"
                    value={formData.prompt}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder={t('admin.panel.aiPromptPlaceholder')}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.panel.aiModel')}
                    </label>
                    <select
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="gpt-5.2">GPT-5.2 (Latest)</option>
                      <option value="gpt-5.2-instant">GPT-5.2 Instant</option>
                      <option value="gpt-4.1">GPT-4.1</option>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-4-turbo-preview">GPT-4 Turbo Preview</option>
                      <option value="gpt-4">GPT-4</option>
                      <option value="gpt-4-32k">GPT-4 32K</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                      <option value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K</option>
                      <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo 1106</option>
                      <option value="o1-preview">O1 Preview</option>
                      <option value="o1-mini">O1 Mini</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.panel.temperature')}
                    </label>
                    <input
                      type="number"
                      name="temperature"
                      value={formData.temperature}
                      onChange={handleInputChange}
                      min="0"
                      max="2"
                      step="0.1"
                      required
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('admin.panel.temperatureHint')}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="developerMode"
                      checked={formData.developerMode || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, developerMode: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t('admin.panel.developerMode')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    {t('admin.panel.developerModeHint')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.catalogMode')}
                  </label>
                  <select
                    name="catalogMode"
                    value={formData.catalogMode || 'standard'}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="standard">{t('admin.panel.catalogModeStandard')}</option>
                    <option value="catering">{t('admin.panel.catalogModeCatering')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{t('admin.panel.catalogModeHint')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.panel.contactEmail')}
                    </label>
                    <input
                      type="email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.panel.contactPhone')}
                    </label>
                    <input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.website')}
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.primaryColor')}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      name="primaryColor"
                      value={formData.primaryColor}
                      onChange={handleInputChange}
                      className="h-10 w-14 cursor-pointer rounded border border-gray-300 p-1"
                    />
                    <input
                      type="text"
                      name="primaryColor"
                      value={formData.primaryColor}
                      onChange={handleInputChange}
                      className="w-28 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="#3b82f6"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.panel.primaryColorHint')}
                  </p>
                </div>

                {/* Pricing Adjustments Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('admin.panel.pricingAdjustments')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{t('admin.panel.pricingAdjustmentsHint')}</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.panel.businessZip')}</label>
                      <input
                        type="text"
                        value={pricingConfig.businessZip}
                        onChange={(e) => setPricingConfig(p => ({ ...p, businessZip: e.target.value }))}
                        placeholder="10115"
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.panel.distanceFactorPer10km')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={pricingConfig.distanceFactorPer10km}
                        onChange={(e) => setPricingConfig(p => ({ ...p, distanceFactorPer10km: e.target.value }))}
                        placeholder="0.02"
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin.panel.distanceFactorHint')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.panel.noParkingFactor')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="1"
                        value={pricingConfig.noParkingFactor}
                        onChange={(e) => setPricingConfig(p => ({ ...p, noParkingFactor: e.target.value }))}
                        placeholder="1.1"
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin.panel.noParkingFactorHint')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.panel.salePercent')}</label>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={pricingConfig.salePercent}
                        onChange={(e) => setPricingConfig(p => ({ ...p, salePercent: e.target.value }))}
                        placeholder="0"
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin.panel.salePercentHint')}</p>
                    </div>
                  </div>
                </div>

                {/* Offer Display Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('admin.panel.offerDisplay')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{t('admin.panel.offerDisplayHint')}</p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pricingConfig.hidePrices}
                        onChange={(e) => setPricingConfig(p => ({ ...p, hidePrices: e.target.checked }))}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-700">{t('admin.panel.hidePrices')}</span>
                        <span className="block text-xs text-gray-500">{t('admin.panel.hidePricesHint')}</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pricingConfig.showPriceListDescription !== false}
                        onChange={(e) => setPricingConfig(p => ({ ...p, showPriceListDescription: e.target.checked }))}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-700">{t('admin.panel.showPriceListDescription')}</span>
                        <span className="block text-xs text-gray-500">{t('admin.panel.showPriceListDescriptionHint')}</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pricingConfig.showEstimateRange}
                        onChange={(e) => setPricingConfig(p => ({ ...p, showEstimateRange: e.target.checked }))}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-700">{t('admin.panel.showEstimateRange')}</span>
                        <span className="block text-xs text-gray-500">{t('admin.panel.showEstimateRangeHint')}</span>
                      </span>
                    </label>
                    {pricingConfig.showEstimateRange && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.panel.estimateRangePercent')}</label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          value={pricingConfig.estimateRangePercent}
                          onChange={(e) => setPricingConfig(p => ({ ...p, estimateRangePercent: e.target.value }))}
                          placeholder="15"
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('admin.panel.estimateRangePercentHint')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Allowed Domains Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.panel.allowedDomains')}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('admin.panel.allowedDomainsHint')}
                  </p>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDomain();
                        }
                      }}
                      placeholder={t('admin.panel.placeholderDomain')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddDomain}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                    >
                      {t('common.add')}
                    </button>
                  </div>
                  <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
                    {allowedDomains.map((domain, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                        <span className="text-gray-700">{domain}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDomain(domain)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          {t('common.remove')}
                        </button>
                      </div>
                    ))}
                    {allowedDomains.length === 0 && (
                      <p className="text-xs text-gray-500 italic">{t('admin.panel.noDomainsAdded')}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    {isEditing ? t('admin.panel.uploadServices') : t('admin.panel.uploadServicesRequired')}
                  </label>
                  {isEditing && selectedChatbot?.services && selectedChatbot.services.length > 0 && (
                    <div className="w-full p-2 border border-gray-300 rounded bg-gray-50 mb-2">
                      <p className="text-sm text-gray-700">
                        {t('admin.panel.servicesCount', { count: selectedChatbot.services.length })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('admin.panel.replaceServicesHint')}
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    required={!isEditing}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {document && (
                    <p className="text-xs text-green-600 mt-1">
                      {t('admin.panel.selectedFile', { name: document.name })}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.panel.supportedFormatsServices')}
                  </p>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isLoading ? t('common.saving') : (isEditing ? t('common.update') : t('common.create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {confirmAction === 'reset' ? t('admin.panel.confirmResetPassword') : t('admin.panel.confirmResendCredentials')}
              </h3>
              <p className="text-gray-600 mb-6">
                {confirmAction === 'reset' 
                  ? t('admin.panel.confirmResetMessage', { name: confirmChatbotName })
                  : t('admin.panel.confirmResendMessage', { name: confirmChatbotName })
                }
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmAction(null);
                    setConfirmChatbotId(null);
                    setConfirmChatbotName(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    if (confirmAction === 'reset') {
                      executeResetPassword();
                    } else if (confirmAction === 'resend') {
                      executeResendCredentials();
                    }
                  }}
                  className={`flex-1 px-4 py-2 text-white rounded-lg ${
                    confirmAction === 'reset' 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Password Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Key className="w-5 h-5 text-indigo-600" />
                  {t('admin.panel.customerLoginCredentials')}
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setGeneratedPassword('');
                    setPasswordChatbotName('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  {t('admin.panel.loginCredentialsFor', { name: passwordChatbotName })}
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-3">
                  <div className="mb-3">
                    <label className="text-xs font-medium text-gray-500 block mb-1">{t('admin.panel.loginUrl')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/customer/login`}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono"
                      />
                      <button
                        onClick={async () => {
                          const success = await copyPasswordToClipboard(`${window.location.origin}/customer/login`);
                          if (success) {
                            alert(t('admin.panel.loginUrlCopied'));
                          } else {
                            alert(t('admin.panel.copyFailed'));
                          }
                        }}
                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="text-xs font-medium text-gray-500 block mb-1">{t('admin.panel.email')}</label>
                    <p className="text-sm font-mono text-gray-900 bg-white px-3 py-2 border border-gray-300 rounded">
                      {chatbots.find(cb => cb.name === passwordChatbotName)?.contactEmail || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">{t('admin.panel.password')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={generatedPassword}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono font-semibold text-indigo-600"
                      />
                      <button
                        onClick={async () => {
                          const success = await copyPasswordToClipboard(generatedPassword);
                          if (success) {
                            alert(t('admin.panel.passwordCopied'));
                          } else {
                            alert(t('admin.panel.copyFailed'));
                          }
                        }}
                        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" />
                        {t('common.copy')}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    {t('admin.panel.savePasswordWarning')}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setGeneratedPassword('');
                    setPasswordChatbotName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={() => {
                    const chatbot = chatbots.find(cb => cb.name === passwordChatbotName);
                    if (chatbot?.contactEmail) {
                      handleResendCredentials(chatbot.id, chatbot.name);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {t('admin.panel.sendViaEmail')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 